import { useRef, useState, type ComponentType, type RefAttributes } from 'react';
import { Form, Input, Button, Space, message, Alert, Radio, Tag, Typography } from 'antd';
import { ArrowLeftOutlined, LockOutlined, ShopOutlined, UserOutlined } from '@ant-design/icons';
import ReCAPTCHA, { type ReCAPTCHAProps } from 'react-google-recaptcha';
import { useDispatch } from 'react-redux';
import { Link, useNavigate } from 'react-router-dom';
import { useMutation } from '@tanstack/react-query';
import { setCredentials } from '@/redux/slices/authSlice';
import { authService, type LoginCredentials } from '@/services/auth.service';
import type { IdentifyTenant } from '@/mocks/auth';
import type { AppDispatch } from '@/redux';
import { ROUTES } from '@/constants/routes';
import { AuthLayout } from '@/components/Layout/AuthLayout';
import { SYSTEM_ROLE_IDS } from '@/mocks/roles';
import { autoInitPosShiftStore } from '@/utils/posShift';

const { Text } = Typography;

type ReCAPTCHAInstance = InstanceType<typeof ReCAPTCHA>;
const ReCAPTCHAComponent = ReCAPTCHA as unknown as ComponentType<
  ReCAPTCHAProps & RefAttributes<ReCAPTCHAInstance>
>;

// Step `email` đổi tên giữ nguyên semantic — "identifier" (phone/email) per cd0a8f8.
type Step = 'email' | 'tenant' | 'password';

const PLAN_COLORS: Record<NonNullable<IdentifyTenant['planTier']>, string> = {
  lite: 'default',
  starter: 'blue',
  professional: 'gold',
  enterprise: 'red',
};

const PLAN_LABELS: Record<NonNullable<IdentifyTenant['planTier']>, string> = {
  lite: 'Lite',
  starter: 'Starter',
  professional: 'Pro',
  enterprise: 'Enterprise',
};

export function LoginPage() {
  const [step, setStep] = useState<Step>('email');
  const [identifier, setIdentifier] = useState('');
  const [tenants, setTenants] = useState<IdentifyTenant[]>([]);
  const [selectedTenant, setSelectedTenant] = useState<IdentifyTenant | null>(null);
  const [errorMsg, setErrorMsg] = useState<string | null>(null);
  const [captchaRequired, setCaptchaRequired] = useState(false);
  const [recaptchaToken, setRecaptchaToken] = useState('');
  const recaptchaRef = useRef<ReCAPTCHAInstance>(null);
  const [identifierForm] = Form.useForm<{ identifier: string }>();
  const [passwordForm] = Form.useForm<{ password: string }>();
  const dispatch = useDispatch<AppDispatch>();
  const navigate = useNavigate();

  const identifyMutation = useMutation({
    mutationFn: (input: string) => authService.identify(input),
    onSuccess: async (data, inputIdentifier) => {
      if (!data.userExists || !data.tenants.length) {
        setErrorMsg('SĐT/email không tồn tại hoặc chưa thuộc tiệm vàng nào.');
        return;
      }
      // Recovery branch — account đã đăng ký nhưng chưa set-password (lost session
      // trước khi complete /auth/set-password). Chuyển sang flow OTP recovery thay
      // vì password input. EG-356.
      if (!data.hasPassword) {
        try {
          const recover = await authService.recoverInit({
            identifier: inputIdentifier,
            preferredChannel: 'zns',
          });
          message.info('Tài khoản chưa đặt mật khẩu — gửi OTP khôi phục');
          // BE response snake_case (ADR-013 §D2). Map sang navigate state
          // camelCase — TS-internal scope (D5 cho phép).
          navigate(ROUTES.auth.otp, {
            state: {
              context: 'recover',
              sessionId: recover.session_id,
              channel: recover.channel,
              availableChannels: recover.available_channels,
              phoneMasked: recover.phone_masked,
              emailMasked: recover.email_masked,
            },
          });
        } catch (err: unknown) {
          const beMsg =
            (err as { response?: { data?: { error?: { message?: string } } } })
              ?.response?.data?.error?.message;
          setErrorMsg(beMsg || 'Không gửi được OTP khôi phục');
        }
        return;
      }
      setIdentifier(inputIdentifier);
      setTenants(data.tenants);
      if (data.tenants.length === 1) {
        const only = data.tenants[0];
        setSelectedTenant(only);
        message.info(`Đăng nhập tiệm: ${only.name}`);
        setStep('password');
      } else {
        setStep('tenant');
      }
    },
    onError: (err: unknown) => {
      const beMsg =
        (err as { response?: { data?: { error?: { message?: string }; message?: string } } })
          ?.response?.data?.error?.message ||
        (err as { response?: { data?: { message?: string } } })?.response?.data?.message ||
        (err as Error)?.message;
      setErrorMsg(typeof beMsg === 'string' ? beMsg : 'Không kiểm tra được thông tin');
    },
  });

  const loginMutation = useMutation({
    mutationFn: (payload: LoginCredentials) => authService.login(payload),
    onSuccess: (data) => {
      // Lưu token + user vào Redux + localStorage. Plan KHÔNG dispatch ở
      // đây — Layout/usePlanData sẽ fetch GET /plan ngay sau khi mount,
      // dispatch setPlan với tier thực từ BE (Plan.slug). Hardcode cũ
      // ('enterprise') gỡ vì BE đã có endpoint + migration eg-630 seed
      // sẵn 4 plans để Plan.slug khớp PlanTier enum.
      const loginUser = {
        ...data.user,
        roleIds: data.user.roleIds.length
          ? data.user.roleIds
          : [SYSTEM_ROLE_IDS.owner],
      };
      dispatch(
        setCredentials({
          accessToken: data.access_token,
          refreshToken: data.refresh_token,
          user: loginUser,
        }),
      );
      autoInitPosShiftStore(loginUser);
      message.success('Đăng nhập thành công');

      if (data.must_set_password) {
        navigate(ROUTES.auth.setPassword, { replace: true });
      } else if (data.must_change_password) {
        navigate(ROUTES.auth.changePassword, { replace: true });
      } else if (data.needs_store_selection) {
        navigate(ROUTES.auth.selectStore, { replace: true });
      } else {
        navigate(ROUTES.root, { replace: true });
      }
    },
    onError: (err: unknown) => {
      const errData = (err as { response?: { data?: { error?: { message?: string; code?: string }; message?: string } } })?.response?.data;
      const beCode = errData?.error?.code;
      if (beCode === 'INVALID_CREDENTIALS') {
        setErrorMsg('Mật khẩu không chính xác');
        return;
      }
      if (beCode === 'CAPTCHA_REQUIRED') {
        setCaptchaRequired(true);
        setRecaptchaToken('');
        setErrorMsg('Vui lòng xác nhận bạn không phải robot');
        return;
      }
      const beMsg = errData?.error?.message || errData?.message || (err as Error)?.message;
      setErrorMsg(typeof beMsg === 'string' ? beMsg : 'Đăng nhập thất bại');
    },
  });

  const handleIdentifierSubmit = (values: { identifier: string }) => {
    setErrorMsg(null);
    identifyMutation.mutate(values.identifier.trim().toLowerCase());
  };

  const handleSelectTenant = () => {
    if (!selectedTenant) {
      setErrorMsg('Vui lòng chọn tiệm vàng');
      return;
    }
    setErrorMsg(null);
    setStep('password');
  };

  const handlePasswordSubmit = (values: { password: string }) => {
    setErrorMsg(null);
    if (!selectedTenant) {
      setErrorMsg('Thiếu thông tin tenant — vui lòng quay lại bước trước.');
      return;
    }
    loginMutation.mutate({
      identifier,
      password: values.password,
      tenant_id: selectedTenant.tenantId,
      ...(recaptchaToken ? { recaptcha_token: recaptchaToken } : {}),
    } satisfies LoginCredentials);
  };

  const handleRecaptchaChange = (token: string | null) => {
    if (!token) return;
    setRecaptchaToken(token);
    // Auto-retry login với token mới
    const values = passwordForm.getFieldsValue();
    if (values.password && selectedTenant) {
      loginMutation.mutate({
        identifier,
        password: values.password,
        tenant_id: selectedTenant.tenantId,
        recaptcha_token: token,
      } satisfies LoginCredentials);
    }
  };

  const handleBack = () => {
    setErrorMsg(null);
    setCaptchaRequired(false);
    setRecaptchaToken('');
    recaptchaRef.current?.reset();
    if (step === 'password') {
      setStep(tenants.length > 1 ? 'tenant' : 'email');
      passwordForm.resetFields();
    } else if (step === 'tenant') {
      setStep('email');
      setSelectedTenant(null);
    }
  };

  const subtitle =
    step === 'email'
      ? 'Đăng nhập để quản lý tiệm vàng'
      : step === 'tenant'
        ? `Tài khoản ${identifier} thuộc nhiều tiệm — chọn tiệm để đăng nhập`
        : selectedTenant
          ? `Đăng nhập vào ${selectedTenant.name}`
          : 'Nhập mật khẩu';

  return (
    <AuthLayout
      title="ERP-Gold"
      subtitle={subtitle}
      footer={
        step === 'email' ? (
          <Space split="·">
            <Link to={ROUTES.auth.forgotPassword}>Quên mật khẩu</Link>
            <Link to={ROUTES.auth.register}>Đăng ký dùng thử</Link>
          </Space>
        ) : (
          <Button
            type="link"
            icon={<ArrowLeftOutlined />}
            onClick={handleBack}
            style={{ padding: 0 }}
          >
            Đổi email khác
          </Button>
        )
      }
    >
      {step === 'email' && (
        <Form
          form={identifierForm}
          onFinish={handleIdentifierSubmit}
          layout="vertical"
          data-testid="login-identifier-form"
        >
          <Form.Item
            label="Số điện thoại hoặc Email"
            name="identifier"
            rules={[
              { required: true, message: 'Vui lòng nhập SĐT hoặc email' },
              {
                validator: (_rule, value) => {
                  if (!value || typeof value !== 'string') return Promise.resolve();
                  const trimmed = value.trim().toLowerCase();
                  const phoneRegex = /^(0|\+?84)\d{9,10}$/;
                  const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
                  if (phoneRegex.test(trimmed) || emailRegex.test(trimmed)) {
                    return Promise.resolve();
                  }
                  return Promise.reject(
                    new Error('Số điện thoại hoặc email không đúng định dạng'),
                  );
                },
              },
            ]}
          >
            <Input
              prefix={<UserOutlined />}
              placeholder="0901234567 hoặc owner@tiemvang.vn"
              autoComplete="username"
              autoFocus
              data-testid="login-identifier-input"
            />
          </Form.Item>
          <Button
            type="primary"
            block
            loading={identifyMutation.isPending}
            htmlType="submit"
            data-testid="login-next-btn"
          >
            Tiếp tục
          </Button>
          {errorMsg && (
            <Alert
              type="error"
              message={errorMsg}
              showIcon
              style={{ marginTop: 12 }}
              data-testid="login-error"
            />
          )}
        </Form>
      )}

      {step === 'tenant' && (
        <Space direction="vertical" style={{ width: '100%' }} size={12}>
          <Radio.Group
            value={selectedTenant?.tenantId}
            onChange={(e) =>
              setSelectedTenant(tenants.find((t) => t.tenantId === e.target.value) ?? null)
            }
            style={{ width: '100%' }}
          >
            <Space direction="vertical" style={{ width: '100%' }} size={8}>
              {tenants.map((t) => (
                <Radio
                  key={t.tenantId}
                  value={t.tenantId}
                  style={{
                    width: '100%',
                    border: '1px solid',
                    borderColor: selectedTenant?.tenantId === t.tenantId ? '#a4193d' : '#f0f0f0',
                    borderRadius: 8,
                    padding: 12,
                    background:
                      selectedTenant?.tenantId === t.tenantId ? '#fff6f8' : '#fff',
                  }}
                >
                  <Space style={{ width: '100%', justifyContent: 'space-between' }}>
                    <Space>
                      <ShopOutlined style={{ color: '#a4193d' }} />
                      <div>
                        <Text strong>{t.name}</Text>
                        <br />
                        <Text type="secondary" style={{ fontSize: 11 }}>
                          {t.tenantCode} · vai trò {t.role}
                          {t.storeCount ? ` · ${t.storeCount} CN` : ''}
                        </Text>
                      </div>
                    </Space>
                    {t.planTier && (
                      <Tag color={PLAN_COLORS[t.planTier]} style={{ marginLeft: 8 }}>
                        {PLAN_LABELS[t.planTier]}
                      </Tag>
                    )}
                  </Space>
                </Radio>
              ))}
            </Space>
          </Radio.Group>
          <Button
            type="primary"
            block
            disabled={!selectedTenant}
            onClick={handleSelectTenant}
            data-testid="login-tenant-confirm"
          >
            Tiếp tục
          </Button>
          {errorMsg && (
            <Alert
              type="error"
              message={errorMsg}
              showIcon
              style={{ marginTop: 12 }}
              data-testid="login-error"
            />
          )}
        </Space>
      )}

      {step === 'password' && (
        <Form
          form={passwordForm}
          onFinish={handlePasswordSubmit}
          layout="vertical"
          data-testid="login-password-form"
        >
          <Form.Item label="Tài khoản">
            <Input value={identifier} disabled prefix={<UserOutlined />} />
          </Form.Item>
          {selectedTenant && tenants.length > 1 && (
            <div
              style={{ marginBottom: 12, padding: 8, background: '#fafafa', borderRadius: 4 }}
            >
              <ShopOutlined style={{ marginRight: 6, color: '#a4193d' }} />
              <Text>{selectedTenant.name}</Text>
            </div>
          )}
          <Form.Item
            label="Mật khẩu"
            name="password"
            rules={[{ required: true, message: 'Vui lòng nhập mật khẩu' }]}
          >
            <Input.Password
              prefix={<LockOutlined />}
              placeholder="Mật khẩu"
              autoComplete="current-password"
              autoFocus
              data-testid="login-password-input"
            />
          </Form.Item>
          {captchaRequired && (
            <div style={{ marginBottom: 12 }}>
              <ReCAPTCHAComponent
                ref={recaptchaRef}
                sitekey={import.meta.env.VITE_RECAPTCHA_SITE_KEY || '6LeIxAcTAAAAAJcZVRqyHh71UMIEGNQ_MXjiZKhI'}
                onChange={handleRecaptchaChange}
              />
            </div>
          )}
          <Button
            type="primary"
            block
            loading={loginMutation.isPending}
            htmlType="submit"
            data-testid="login-submit-btn"
          >
            Đăng nhập
          </Button>
          {errorMsg && (
            <Alert
              type="error"
              message={errorMsg}
              showIcon
              style={{ marginTop: 12 }}
              data-testid="login-error"
            />
          )}
        </Form>
      )}
    </AuthLayout>
  );
}
